import React, { useState } from "react";
import { Button, Col, Form, Input, Progress, Row, Space } from "antd";
import {
  ArrowRightOutlined,
  DeleteFilled,
  HolderOutlined,
  LeftOutlined,
  PlusCircleFilled,
  PlusCircleOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import {
  ProgressInfo,
  ProgressLabel,
  ProgressSection,
  StepCounter,
  ThemeChatMassage,
  ThemeCoverBox,
  ThemeFitToScreenCard,
  ThemeProgress,
  ThemeStepsWrapper,
  ThemeStudyCover,
} from "./SurveysStyle";
import ChatBox from "../../Components/ChatBox";
import {
  ThemeButton,
  ThemeFilterBtn,
  ThemeForm,
  ThemeInput,
  ThemeSwitch,
  XThemeSelect,
} from "../Styles/Theme";
import {
  WelcomeMessage,
  Rating,
  RadioButtons,
  CheckBoxButtons,
  DragListElement,
  QstType,
} from "./SurveyComponents/QstFormat/QstFormat";
import {
  ThemeDragList,
  ThemeQSTCard,
  ThemeQstName,
  ThemeQstTitle,
} from "./SurveyComponents/QstFormat/QstStyle";
import styled from "styled-components";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const StudyCover = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedOption, setSelectedOption] = useState(null);

  const totalSteps = 4;
  const progress = ((currentStep + 1) / totalSteps) * 100;

  const nextStep = () => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1);
      setSelectedOption(null);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      setSelectedOption(null);
    }
  };

  const handleOptionSelect = (option) => {
    setSelectedOption(option);
  };

  // Sortable Question Card Component
  const SortableQuestionCard = ({ question }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: question.id });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      boxShadow: isDragging ? "0 4px 8px rgba(0, 0, 0, 0.1)" : "none",
    };

    return (
      <ThemeCoverBox
        ref={setNodeRef}
        style={style}
        {...attributes}
        className={`mb-3 ${question.id === "q1" ? "background" : ""}`}
        title={
          <Row gutter={[16, 16]} align="middle">
            <Col xs={20}>{question.title}</Col>
            <Col xs={4} className="text-end">
              <Button
                type="text"
                size="small"
                icon={<img src="/images/delete-icon.svg" alt="Delete" />}
              />
            </Col>
          </Row>
        }
      >
        <Row gutter={[16, 16]} align="middle">
          <Col xs={2}>
            <img src={question.icon} alt="" />
          </Col>
          <Col xs={20}>
            <div className="defaultCardText">{question.text}</div>
          </Col>
          <Col xs={2} className="text-end">
            <div {...listeners} className="dragPointer">
              <HolderOutlined />
            </div>
          </Col>
        </Row>
      </ThemeCoverBox>
    );
  };

  const [questions, setQuestions] = useState([
    {
      id: "q1",
      title: "Questions 1",
      text: "Do you believe climate change is a serious threat to the United States?",
      icon: "/images/user-experience-icon.svg",
    },
    {
      id: "q2",
      title: "Question 2",
      text: "How concerned are you personally about the effects of climate change in your lifetime?",
      icon: "/images/radio-icon.svg",
    },
    {
      id: "q3",
      title: "Question 3",
      text: "Which of the following environmental policies do you support?",
      icon: "/images/check-box-icon.svg",
    },
    {
      id: "q4",
      title: "Question 4",
      text: "Who should take the lead in addressing climate change?",
      icon: "/images/radio-icon.svg",
    },
  ]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      setQuestions((prevQuestions) => {
        const activeIndex = prevQuestions.findIndex(
          (question) => question.id === active.id
        );
        const overIndex = prevQuestions.findIndex(
          (question) => question.id === over.id
        );

        const newQuestions = [...prevQuestions];
        const [removed] = newQuestions.splice(activeIndex, 1);
        newQuestions.splice(overIndex, 0, removed);

        return newQuestions;
      });
    }
  };

  return (
    <>
      <Row gutter={[30, 16]}>
        <Col xs={24} sm={24} md={24} lg={7} xl={6}>
          <ThemeFitToScreenCard className="coverWrapper" title="Cover">
            <ThemeStudyCover>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={questions.map((question) => question.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <ThemeCoverBox className="mb-3">
                    <div className="defaultCardText">
                      <span>
                        <img src="/images/cover-icon.svg" alt="Cover" />
                      </span>
                      We're interested in hearing how people, especially younger
                      generations, view important issues like climate change,
                      politics, and the future of our country. Your responses
                      will remain confidential and will be used only for
                      research purposes.
                    </div>
                  </ThemeCoverBox>

                  {questions.map((question) => (
                    <SortableQuestionCard
                      key={question.id}
                      question={question}
                    />
                  ))}

                  <ThemeFilterBtn
                    className="fullWidth"
                    size="large"
                    shape="square"
                    icon={<PlusCircleFilled />}
                  >
                    Add New Question
                  </ThemeFilterBtn>
                </SortableContext>
              </DndContext>
            </ThemeStudyCover>
          </ThemeFitToScreenCard>
        </Col>
        <Col xs={24} sm={24} md={24} lg={10} xl={12}>
          <ThemeStepsWrapper
            title={
              <ProgressSection>
                <ProgressInfo>
                  <ProgressLabel>Progress</ProgressLabel>
                  <ThemeProgress
                    percent={progress}
                    showInfo={false}
                    strokeWidth={6}
                  />
                </ProgressInfo>
                <StepCounter>
                  {currentStep + 1}/{totalSteps}
                </StepCounter>
              </ProgressSection>
            }
          >
            <Row gutter={[16, 16]}>
              <Col xs={24}>
                <div className="minHeight">
                  {currentStep === 0 && (
                    <>
                      <ThemeQstTitle>
                        <span className="grayBG">Q1</span>
                      </ThemeQstTitle>
                      <ThemeQstName className="darkColor strong largeFont my-3">
                        Who should take the lead in addressing climate change?
                      </ThemeQstName>
                      <RadioButtons />
                    </>
                  )}
                  {currentStep === 1 && (
                    <>
                      <ThemeQstTitle>
                        <span className="grayBG">Q2</span>
                      </ThemeQstTitle>
                      <ThemeQstName className="darkColor strong largeFont my-3">
                        Who should take the lead in addressing climate change?
                      </ThemeQstName>
                      <CheckBoxButtons />
                    </>
                  )}
                  {currentStep === 2 && (
                    <>
                      <ThemeQstTitle>
                        <span className="grayBG">Q3</span>
                      </ThemeQstTitle>
                      <ThemeQstName className="darkColor strong largeFont my-3">
                        Who should take the lead in addressing climate change?
                      </ThemeQstName>
                      <DragListElement />
                    </>
                  )}
                  {currentStep === 3 && (
                    <>
                      <ThemeQstTitle>
                        <span className="grayBG">Q4</span>
                      </ThemeQstTitle>
                      <ThemeQstName className="darkColor strong largeFont my-3">
                        Select Question
                      </ThemeQstName>
                      <QstType />
                    </>
                  )}
                </div>
              </Col>
              <Col xs={24} className="text-end">
                <ThemeButton
                  className="noBackground me-3"
                  size="large"
                  shape="circle"
                  icon={<LeftOutlined />}
                  onClick={prevStep}
                  disabled={currentStep === 0}
                />
                <ThemeButton
                  size="large"
                  shape="circle"
                  icon={<ArrowRightOutlined />}
                  className="primary"
                  onClick={nextStep}
                />
              </Col>
            </Row>
          </ThemeStepsWrapper>
        </Col>
        <Col xs={24} sm={24} md={24} lg={7} xl={6}>
          <ThemeForm layout="vertical">
            <ThemeFitToScreenCard className="coverWrapper">
              <ThemeStudyCover>
                <Row gutter={[16, 0]}>
                  <Col xs={24}>
                    <ThemeForm.Item
                      className="labelStyle1"
                      label="Alter question format"
                      name="alterQuestionFormat"
                    >
                      <XThemeSelect
                        className="fullWidth noRadius"
                        shape="square"
                        size="large"
                        defaultValue={1}
                        options={[
                          { value: 1, label: "Single Choice" },
                          { value: 2, label: "Lorem ipsum dolor 1" },
                          { value: 3, label: "Lorem ipsum dolor 2" },
                        ]}
                      />
                    </ThemeForm.Item>
                  </Col>
                  <Col xs={24}>
                    <ThemeForm.Item
                      className="labelStyle1"
                      label="Grid Format"
                      name="gridFormat"
                    >
                      <XThemeSelect
                        className="fullWidth noRadius"
                        shape="square"
                        size="large"
                        defaultValue={1}
                        options={[
                          { value: 1, label: "Radio Buttons" },
                          { value: 2, label: "Checkboxes" },
                          { value: 3, label: "Dropdown" },
                          { value: 4, label: "Textbox" },
                          { value: 5, label: "Scale" },
                          { value: 6, label: "Picture" },
                          { value: 7, label: "Single Choice" },
                          { value: 8, label: "Multi Select" },
                          { value: 9, label: "File" },
                          { value: 10, label: "Email" },
                          { value: 11, label: "Number" },
                          { value: 12, label: "Matrix" },
                          { value: 13, label: "Ranking" },
                          { value: 14, label: "Video" },
                        ]}
                      />
                    </ThemeForm.Item>
                  </Col>
                  <Col xs={24}>
                    <ThemeForm.Item
                      className="labelStyle2"
                      label="Required"
                      name="required"
                      colon={false}
                      layout="horizontal"
                    >
                      <span>
                        <ThemeSwitch
                          className="positionRight"
                          defaultChecked={true}
                        />
                      </span>
                    </ThemeForm.Item>
                  </Col>
                </Row>
              </ThemeStudyCover>
            </ThemeFitToScreenCard>
          </ThemeForm>
        </Col>
      </Row>
    </>
  );
};

export default StudyCover;
